/* eslint-disable @typescript-eslint/no-explicit-any */

import { isAxiosError } from 'axios';

import { api, ApiResponse } from '@/api/api';
import { SCREENING_URLS } from '@/api/url';
import { Screening } from '@/types/Screening';
import { ScreeningStatus, ScreeningType } from '@/types/enums';

export interface GetScreeningsParams {
  page?: number;
  limit?: number;
  status?: ScreeningStatus[];
  awaiting_review?: boolean;
}

export interface GetScreeningsResponse {
  screenings: Screening[];
  total_count: number;
  current_page: number;
  total_pages: number;
  limit: number;
}

export const apiGetScreenings = async ({
  params
}: {
  params: GetScreeningsParams;
}): Promise<ApiResponse<GetScreeningsResponse>> => {
  try {
    // Fix: If status is an array, convert to comma-separated string for query param
    const queryParams = {
      ...params,
      status: Array.isArray(params.status) ? params.status.join(',') : params.status
    };

    const response = await api.get<ApiResponse<GetScreeningsResponse>>(SCREENING_URLS.GET_ALL, {
      params: queryParams
    });

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetScreeningStatisticsResponse {
  total_sessions: number;
  scheduled_sessions: number;
  completed_sessions: number;
  in_progress_sessions: number;
  expired_sessions: number;
}

export const apiGetScreeningStatistics = async (): Promise<
  ApiResponse<GetScreeningStatisticsResponse>
> => {
  try {
    const response = await api.get<ApiResponse<GetScreeningStatisticsResponse>>(
      SCREENING_URLS.GET_STATISTICS
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface CreateScreeningPayload {
  screening_type: ScreeningType;
  job_id?: string;
  candidate_id: string;
  session_name: string;
  deadline: number;
  timezone: string;
  interview_title: string;
  interview_description: string;
  retake_limit: number;
  session_time_limit: number;
  questions: string[];
}

export const apiCreateScreening = async ({
  payload
}: {
  payload: CreateScreeningPayload;
}): Promise<ApiResponse<Screening>> => {
  try {
    const response = await api.post<ApiResponse<Screening>>(SCREENING_URLS.CREATE, payload);

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiResendScreening = async (screeningId: string): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post<ApiResponse<void>>(SCREENING_URLS.RESEND(screeningId));

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiGetScreening = async (screeningId: string): Promise<ApiResponse<Screening>> => {
  try {
    const response = await api.get<ApiResponse<Screening>>(SCREENING_URLS.GET(screeningId));

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

interface UpdateScreeningPayload {
  session_name: string;
  deadline: number;
  timezone: string;
  interview_title: string;
  interview_description: string;
  retake_limit: number;
  session_time_limit: number;
  questions: string[];
}

export const apiUpdateScreening = async ({
  screeningId,
  payload
}: {
  screeningId: string;
  payload: UpdateScreeningPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put<ApiResponse<void>>(SCREENING_URLS.UPDATE(screeningId), payload);

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiCancelScreening = async (screeningId: string): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete<ApiResponse<void>>(SCREENING_URLS.CANCEL(screeningId));
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

export interface CreateMockScreeningPayload {
  screening_type: ScreeningType;
  job_id?: string;
  candidate_id: string;
  quality_level: 'very_strong' | 'strong' | 'medium' | 'weak' | 'very_weak';
}

export const apiCreateMockScreening = async ({
  screening_type,
  job_id,
  candidate_id,
  quality_level
}: CreateMockScreeningPayload): Promise<ApiResponse<any>> => {
  try {
    const response = await api.post<ApiResponse<any>>(SCREENING_URLS.CREATE_MOCK, {
      screening_type,
      job_id,
      candidate_id,
      quality_level
    });
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};

interface UpdateScreeningQuestionVideoDurationPayload {
  question_id: string;
  video_duration: number;
}

export const apiUpdateScreeningQuestionVideoDuration = async ({
  screeningId,
  payload
}: {
  screeningId: string;
  payload: UpdateScreeningQuestionVideoDurationPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put<ApiResponse<void>>(
      SCREENING_URLS.UPDATE_QUESTION_VIDEO_DURATION(screeningId),
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiReviewScreening = async (screeningId: string): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post<ApiResponse<void>>(SCREENING_URLS.REVIEW(screeningId));
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }
    throw error;
  }
};
